/* eslint-disable react/no-access-state-in-setstate */
import React from 'react';
import { withRouter } from 'react-router-dom';
import {
  get, find, sortBy, concat, isEmpty,
} from 'lodash';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { nanoid } from 'nanoid';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import MUIDataTable from 'mui-datatables';
import { Stop } from '@mui/icons-material';
import { Paper, Grid, Typography } from '@mui/material';
import Spinner from '../../common/spinner';
import CustomToolbar from '../custom-toolbar';
import withPersistentSettings from '../../common/table-with-persistent-settings';
import DateRangeFilter from '../../common/date-range-filter';
import WarnBeforeDeleting from '../../common/warn-before-deleting';
import {
  shouldFetchList,
  localeLongDateFormat,
  localeTimeFormat,
  RunStatusNames,
  timeZoneHint,
  FeatureFlagTypes,
  DataDriveRunTypes,
} from '../../../lib/utils';
import { filterChipText, shouldExcludeDate } from '../../../lib/table-utils';
import marketActions from '../../../actions/markets';
import runActions from '../../../actions/runs';
import wranglerActions from '../../../actions/wranglers';
import zoneActions from '../../../actions/zones';
import locationActions from '../../../actions/locations';
import * as snackbarActions from '../../../actions/snackbar';
import vehicleActions from '../../../actions/vehicles';
import featureFlagsActions from '../../../actions/featureFlags';
import { RunManagerModal } from './run-manager-modal';
import RunActionDropdown from './run-action-dropdown';
import { RunTaskModal } from './run-task-modal';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(localizedFormat);

const styles = {
  root: {
    width: '95%',
    margin: '0 auto',
    overflowX: 'auto',
    borderTopRadius: 0,
  },
};

const NAME = 'Run Name';
const START_TIME = 'Scheduled Start Time (local)';
const ASSIGNEE = 'Assignee';
const ROUTE_COL_NAME = 'Route';
const DROPOFF = 'Dropoff';
const REBALANCE = 'Rebalance';
const STATUS = 'Status';
const SCORE = 'Score';
const EDIT_RUN = 'Edit Run';
const COPY_RUN = 'Copy Run';
const DELETE_RUN = 'Delete Run';
const DELETE_WARN = 'Are you sure you want to delete this Run?';
const DELETE_CONFIRM = 'Successfully deleted Run';
const DELETE_ERROR = 'Error deleting Run';

class BaseRunTable extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isRunModalOpen: false,
      editing: false,
      editRun: {},
      warnBeforeDeleting: false,
      isRunTaskModalOpen: false,
      displayModalRadioSelector: false,
    };
    this.interval = React.createRef();

    this.handleAddRun = this.handleAddRun.bind(this);
    this.handleEditRun = this.handleEditRun.bind(this);
    this.handleCopyRun = this.handleCopyRun.bind(this);
    this.handleCloseRunModal = this.handleCloseRunModal.bind(this);
    this.handleSaveRun = this.handleSaveRun.bind(this);
    this.getLocationsForZone = this.getLocationsForZone.bind(this);
    this.overallTarget = this.overallTarget.bind(this);
    this.vehicleCounts = this.vehicleCounts.bind(this);
    this.onDelete = this.onDelete.bind(this);
    this.getVehiclesForZone = this.getVehiclesForZone.bind(this);
    this.getZonesForRun = this.getZonesForRun.bind(this);
    this.getVehiclesAndLocations = this.getVehiclesAndLocations.bind(this);
    this.handleDropdownChange = this.handleDropdownChange.bind(this);
    this.handleViewClosedRun = this.handleViewClosedRun.bind(this);
    this.getRuns = this.getRuns.bind(this);
  }

  componentDidMount() {
    const {
      match,
      runs,
      zones,
      fetchZones,
      wranglers,
      marketLocations,
      marketVehicles,
      fetchWranglers,
      fetchLocations,
      fetchVehicles,
      featureFlags,
      fetchFeatureFlags,
    } = this.props;
    const marketId = get(match, 'params.marketId');
    if (marketId) {
      if (shouldFetchList(marketId, runs)) {
        this.getRuns();
      }
      if (shouldFetchList(marketId, featureFlags)) fetchFeatureFlags(marketId);
      if (shouldFetchList(marketId, wranglers)) fetchWranglers({ market_id: marketId });
      if (shouldFetchList(marketId, zones)) fetchZones({ market_id: marketId });
      if (shouldFetchList(marketId, marketLocations)) fetchLocations({ market_id: marketId });
      if (shouldFetchList(marketId, marketVehicles)) fetchVehicles({ market_id: marketId });
    }
  }

  onDelete(run) {
    this.setState({ editRun: run, warnBeforeDeleting: true });
  }

  get runs() {
    const runs = get(this, 'props.runs', []);
    return sortBy(runs, (run) => run.name.toLowerCase());
  }

  get tableOptions() {
    const { runs, tableOptions } = this.props;
    const basicOptions = tableOptions;
    basicOptions.onRowClick = (rowData) => {
      const matchingRun = find(runs, (run) => run._id === rowData[0]);
      if (matchingRun.status === RunStatusNames.CLOSED && matchingRun.isDynamic) {
        this.handleViewClosedRun(matchingRun);
      } else {
        this.handleEditRun(matchingRun);
      }
    };

    basicOptions.customToolbar = () => (
      <>
        <CustomToolbar onClick={this.getRuns} type="Refresh" />
        <CustomToolbar onClick={this.handleAddRun} type="Add" />
      </>
    );
    return basicOptions;
  }

  get columns() {
    const {
      addToColumnData, selectedFilters, primarySort, runs, selectedMarket,
    } = this.props;
    const tz = get(selectedMarket, 'tz', 'America/New_York');

    const columnData = [
      {
        name: 'ID',
        options: {
          filter: false,
          display: 'excluded',
        },
      },
      {
        name: NAME,
        options: {
          filter: false,
          sort: false,
        },
      },
      {
        name: START_TIME,
        options: {
          sort: false,
          hint: timeZoneHint(tz),
          customBodyRender: (value) => {
            if (value) {
              return (
                <>
                  {value.hasTime ? (
                    <>
                      <Typography>
                        {dayjs(value.date).tz(tz).format(localeLongDateFormat)}
                      </Typography>
                      <Typography>{dayjs(value.date).tz(tz).format(localeTimeFormat)}</Typography>
                    </>
                  ) : (
                    <Typography>
                      {dayjs(value.date).tz(tz).format(localeLongDateFormat)}
                    </Typography>
                  )}
                </>
              );
            } else {
              return '';
            }
          },
          filterType: 'custom',
          customFilterListOptions: {
            render: (dates) => filterChipText(dates, START_TIME),
          },
          filterOptions: {
            logic: (date, filters) => shouldExcludeDate(date.date, filters),
            display: (filterList, onChange, index, column) => (
              <DateRangeFilter
                filterList={filterList}
                onChange={onChange}
                index={index}
                column={column}
                name={START_TIME}
              />
            ),
          },
        },
      },
      {
        name: ASSIGNEE,
        options: {
          sort: false,
          customBodyRender: (assignees) => {
            if (assignees) {
              return (
                <Grid>
                  {assignees.map((a, i) => (
                    <Typography
                      key={a}
                      noWrap
                    >
                      {a}
                      {assignees.length && i < assignees.length - 1 ? ',' : ''}
                    </Typography>
                  ))}
                </Grid>
              );
            } else {
              return '';
            }
          },
          filterType: 'multiselect',
          filterOptions: {
            logic: (assignees, filters) => {
              if (assignees.length > 1) {
                return !filters.includes(assignees[0]) && !filters.includes(assignees[1]);
              } else if (assignees.length > 0) return !filters.includes(assignees[0]);
              else if (assignees.length === 0 && filters.length > 0) return true;
              return false;
            },
          },
          customFilterListOptions: {
            render: (v) => `${ASSIGNEE}: ${v}`,
          },
        },
      },
      {
        name: ROUTE_COL_NAME,
        options: {
          filter: false,
          sort: false,
          customBodyRender: (data) => {
            if (data) {
              if (data.length > 0 && data[0].isDynamic) {
                return (
                  <Grid>
                    <div style={{ display: 'flex' }}>
                      <Stop style={{ color: data[0].hexColor }} />
                      <Typography
                        style={{ marginTop: 2 }}
                        noWrap
                      >
                        DataDrive
                      </Typography>
                    </div>
                  </Grid>
                );
              } else {
                return (
                  <Grid>
                    {data.map((r) => (
                      <div
                        style={{ display: 'flex' }}
                        key={r._id}
                      >
                        <Stop style={{ color: r.hexColor }} />
                        <Typography
                          style={{ marginTop: 2 }}
                          noWrap
                        >
                          {r.name}
                        </Typography>
                      </div>
                    ))}
                  </Grid>
                );
              }
            } else {
              return '';
            }
          },
        },
      },
      {
        name: DROPOFF,
        options: {
          filter: false,
          sort: false,
          customBodyRender: (data) => {
            if (data.deploysCompleted || data.deploysRecommended) {
              return `${data.deploysCompleted}/${data.deploysRecommended}`;
            } else {
              return '0/0';
            }
          },
        },
      },
      {
        name: REBALANCE,
        options: {
          filter: false,
          sort: false,
          customBodyRender: (data) => {
            if (data.rebalancesRecommended) {
              return `${data.rebalancesCompleted}/${data.rebalancesRecommended}`;
            } else {
              return '0/0';
            }
          },
        },
      },
      {
        name: STATUS,
        options: {
          sort: false,
          customFilterListOptions: {
            render: (v) => `${STATUS}: ${v}`,
          },
        },
      },
      {
        name: SCORE,
        options: {
          filter: false,
          sort: false,
          hint: "Percentage of a run's recommended tasks that were completed",
          customBodyRender: (data) => {
            if (data.adherenceScore) {
              return `${Math.round(data.adherenceScore * 100)}%`;
            } else {
              return '0%';
            }
          },
        },
      },
      {
        name: '',
        options: {
          filter: false,
          sort: false,
          viewColumns: false,
          customBodyRender: (run) => {
            if (run) {
              return (
                <RunActionDropdown
                  isRun
                  matchingRun={run}
                  isDynamic={get(run, 'isDynamic', false)}
                  onChange={this.handleDropdownChange}
                />
              );
            } else {
              return '';
            }
          },
        },
      },
    ];

    return addToColumnData(columnData, selectedFilters, primarySort);
  }

  get data() {
    const { runs = [] } = this.props;
    const data = runs.map((run) => {
      const runWranglers = get(run, 'wranglers', []);
      const renderWranglers = runWranglers.map((w) => `${w.firstName} ${w.lastName}`);
      const tableData = [
        get(run, '_id', ''),
        get(run, 'name', ''),
        get(run, 'startDate', ''),
        renderWranglers,
        get(run, 'routes', []),
        get(run, 'metrics', {}),
        get(run, 'metrics', {}),
        get(run, 'status', ''),
        get(run, 'metrics', {}),
        run,
      ];
      return tableData;
    });

    return data;
  }

  getRuns() {
    clearInterval(this.interval.current);
    const { marketId } = get(this, 'props.match.params');
    const { fetchRuns, showErrorSnackbar } = this.props;
    fetchRuns({ market_id: marketId }).catch((error) => {
      showErrorSnackbar(`Error fetching runs: ${error}`);
    });
    this.interval.current = setInterval(this.getRuns, 600000); // 5 minutes
  }

  getVehiclesAndLocations(run) {
    let vehiclesAndLocations = [];
    const zones = this.getZonesForRun(run);
    for (const zone of zones) {
      if (zone.isPickup) {
        const vehicles = this.getVehiclesForZone(zone);
        vehiclesAndLocations = concat(vehiclesAndLocations, vehicles);
      } else {
        const locations = this.getLocationsForZone(zone);
        vehiclesAndLocations = concat(vehiclesAndLocations, locations);
      }
    }
    return vehiclesAndLocations;
  }

  getZonesForRun(run) {
    const { allZones = [] } = this.props;
    const zones = [];
    if (run) {
      for (const routeId of run.routeIds) {
        for (const zone of allZones) {
          if (zone._id === routeId) {
            zones.push(zone);
          }
        }
      }
    }
    return zones;
  }

  getVehiclesForZone(zone) {
    const { marketVehicles = [] } = this.props;
    const vehicles = [];
    if (zone) {
      for (const vehicleId of zone.vehicleIds) {
        for (const veh of marketVehicles) {
          if (veh.humanId === vehicleId) {
            vehicles.push(veh);
            break;
          }
        }
      }
    }
    return vehicles;
  }

  getLocationsForZone(zone) {
    const { marketLocations = [] } = this.props;
    const locations = [];
    if (zone) {
      for (const location of zone.locations) {
        for (const loc of marketLocations) {
          if (loc._id === location) {
            locations.push(loc);
            break;
          }
        }
      }
    }
    return locations;
  }

  vehicleCounts(zone) {
    const locations = this.getLocationsForZone(zone);
    let count = 0;
    for (const location of locations) {
      const vehicleCount = get(location, 'vehicleIds', []).length;
      count += vehicleCount;
    }
    return count;
  }

  overallTarget(zone) {
    const locations = this.getLocationsForZone(zone);
    let current = 0;
    for (const location of locations) {
      const target = get(location, 'targetVehicleCounts.link', 0);
      current += target;
    }
    return current;
  }

  handleAddRun() {
    const { marketId } = get(this, 'props.match.params');
    const { featureFlags = [] } = this.props;
    const flags = featureFlags.filter((flag) => flag.type === FeatureFlagTypes.DATA_DRIVE);
    const newRun = {
      _id: nanoid(24),
      status: RunStatusNames.TODO,
      routeIds: [],
      marketId,
    };

    this.setState({
      isRunModalOpen: true,
      editRun: newRun,
      editing: false,
      warnBeforeDeleting: false,
      displayModalRadioSelector: !isEmpty(flags),
    });
  }

  handleEditRun(run) {
    this.setState({
      isRunModalOpen: true,
      editRun: run,
      editing: true,
      warnBeforeDeleting: false,
      displayModalRadioSelector: false,
    });
  }

  handleCopyRun(run) {
    const copiedRun = {
      _id: nanoid(24),
      status: RunStatusNames.TODO,
      marketId: run.marketId,
      startDate: run.startDate,
      routeIds: run.routeIds,
      name: `${run.name} Copy`,
      isDynamic: run.isDynamic,
    };
    this.setState({
      isRunModalOpen: true,
      editRun: copiedRun,
      editing: false,
      warnBeforeDeleting: false,
      displayModalRadioSelector: false,
    });
  }

  handleViewClosedRun(run) {
    this.props.fetchRunTasks(run._id);
    this.setState({
      ...this.state,
      isRunTaskModalOpen: true,
      editRun: run,
    });
  }

  handleSaveRun(run, runChanges) {
    const { editing } = this.state;
    const { showSuccessSnackbar, showErrorSnackbar } = this.props;
    const { marketId } = get(this, 'props.match.params');

    let query;
    if (marketId) {
      query = { market_id: marketId };
    }

    if (!runChanges.isDynamic) {
      if (!runChanges.name) {
        showErrorSnackbar('Please enter a name.');
        return;
      }
      if (!runChanges.startDate) {
        showErrorSnackbar('Please select a start date.');
        return;
      }
      if (!runChanges.routeIds || runChanges.routeIds.length < 1) {
        showErrorSnackbar('Please select a route.');
        return;
      }
      if (runChanges.wranglerIds && runChanges.wranglerIds.length > 2) {
        showErrorSnackbar('You can only select a maximum of 2 wranglers.');
        return;
      }
    } else if (!editing && runChanges.isDynamic === true) {
      if (!runChanges.type) {
        showErrorSnackbar('Please select a run type.');
        return;
      }
      if ((!runChanges.deployCount && runChanges.type !== DataDriveRunTypes.REBALANCE)
      || (runChanges.deployCount && runChanges?.deployCount < 1)) {
        showErrorSnackbar('Please enter a positive number of scooters to deploy.');
        return;
      }
      if (!runChanges?.duration || runChanges?.duration
        && (Number.isNaN(runChanges?.duration) || runChanges?.duration < 0.5)
      ) {
        showErrorSnackbar('Please enter a positive number of hours for shift length.');
        return;
      }
      if (runChanges?.vanAssignees) {
        for (const key in runChanges.vanAssignees) {
          if (runChanges.vanAssignees[key].length > 2) {
            showErrorSnackbar('You can only select a maximum of 2 wranglers for each pending run.');
            return;
          }
        }
      }
    } else if (editing && run.isDynamic === true) {
      if (runChanges.wranglerIds && runChanges.wranglerIds.length > 2) {
        showErrorSnackbar('You can only select a maximum of 2 wranglers.');
        return;
      }
    }

    if (run && runChanges) {
      if (editing) {
        this.props
          .updateRun(run._id, runChanges)
          .then(() => showSuccessSnackbar('Successfully updated run'))
          .then(() => this.props.fetchRuns(query))
          .then(() => this.props.fetchZones(query))
          .then(() => this.props.fetchFeatureFlags(marketId))
          .catch((error) => {
            showErrorSnackbar(`Error updating run: ${error}`);
          });
      } else if (runChanges && !runChanges.isDynamic) {
        this.props
          .createRun(runChanges)
          .then(() => showSuccessSnackbar('Successfully created run'))
          .then(() => this.props.fetchRuns(query))
          .then(() => this.props.fetchZones(query))
          .then(() => this.props.fetchFeatureFlags(marketId))
          .catch((error) => {
            showErrorSnackbar(`Error creating run: ${error}`);
          });
      } else {
        this.props
          .createDynamicRun(runChanges)
          .then(() => showSuccessSnackbar('Successfully created run'))
          .then(() => this.props.fetchRuns(query))
          .then(() => this.props.fetchZones(query))
          .then(() => this.props.fetchFeatureFlags(marketId))
          .catch((error) => {
            showErrorSnackbar(`Error creating run: ${error}`);
          });
      }
    }
    this.setState({
      isRunModalOpen: false,
      editRun: {},
      editing: false,
      warnBeforeDeleting: false,
      displayModalRadioSelector: false,
    });
  }

  handleCloseRunModal() {
    this.setState({
      isRunModalOpen: false,
      editRun: {},
      editing: false,
      warnBeforeDeleting: false,
      isRunTaskModalOpen: false,
      displayModalRadioSelector: false,
    });
  }

  handleDropdownChange(event, run) {
    const selected = event.target.getAttribute('name');
    switch (selected) {
      case EDIT_RUN:
        this.handleEditRun(run);
        break;
      case COPY_RUN:
        this.handleCopyRun(run);
        break;
      case DELETE_RUN:
        this.onDelete(run);
        break;
      default:
        break;
    }
  }

  render() {
    const {
      getTitleFromData, wranglers, zones,
      showSuccessSnackbar, showErrorSnackbar,
      marketWarehouses = [], allZones = [], selectedMarket,
      deployableVehicles = [], featureFlags = [],
      equityDeployMarkets,
    } = this.props;
    const { marketId } = get(this, 'props.match.params');
    const {
      isRunModalOpen,
      editRun,
      warnBeforeDeleting,
      isRunTaskModalOpen,
      displayModalRadioSelector,
      editing,
    } = this.state;
    const displayData = this.data;
    const tz = get(selectedMarket, 'tz', 'America/New_York');
    const flags = featureFlags.filter((flag) => flag.type === FeatureFlagTypes.DATA_DRIVE);
    return (
      <div>
        {this.props.isLoading
          ? <Spinner />
          : (
            <Paper style={styles.root} sx={{ mt: 3 }}>
              <MUIDataTable
                title={getTitleFromData('Runs', displayData)}
                data={displayData}
                columns={this.columns}
                options={this.tableOptions}
              />
              {isRunTaskModalOpen && (
                <RunTaskModal
                  open={isRunTaskModalOpen}
                  run={editRun}
                  handleClose={this.handleCloseRunModal}
                  tz={tz}
                />
              )}
              {isRunModalOpen && editRun && !warnBeforeDeleting && (
                <RunManagerModal
                  open={isRunModalOpen}
                  zones={zones}
                  wranglers={wranglers}
                  handleSave={this.handleSaveRun}
                  handleClose={this.handleCloseRunModal}
                  run={editRun}
                  overallTarget={this.overallTarget}
                  vehicleCounts={this.vehicleCounts}
                  warehouses={marketWarehouses}
                  allZones={allZones}
                  getVehiclesAndLocations={this.getVehiclesAndLocations}
                  selectedMarket={selectedMarket}
                  tz={tz}
                  deployableVehicles={deployableVehicles}
                  flags={flags}
                  isDynamic={get(editRun, 'isDynamic', false)}
                  displayModalRadioSelector={displayModalRadioSelector}
                  editing={editing}
                  equityDeployMarkets={equityDeployMarkets}
                />
              )}
              {editRun && warnBeforeDeleting && (
                <WarnBeforeDeleting
                  isOpen={warnBeforeDeleting}
                  message={DELETE_WARN}
                  onCancel={() => {
                    this.setState({ warnBeforeDeleting: false });
                  }}
                  onConfirm={() => {
                    this.props
                      .deleteRun(editRun._id)
                      .then(() => {
                        showSuccessSnackbar(DELETE_CONFIRM);
                        this.setState({ warnBeforeDeleting: false });
                        this.props.fetchRuns({ market_id: marketId });
                        this.props.fetchZones({ market_id: marketId });
                      })
                      .catch((error) => {
                        showErrorSnackbar(`${DELETE_ERROR}: ${error}`);
                      });
                  }}
                />
              )}
            </Paper>
          )}
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => bindActionCreators({
  ...marketActions,
  ...runActions,
  ...wranglerActions,
  ...zoneActions,
  ...locationActions,
  ...snackbarActions,
  ...vehicleActions,
  ...featureFlagsActions,
}, dispatch);

const mapStateToProps = ({
  markets, runs, wranglers, zones, locations, vehicles, featureFlags, settings,
}) => ({
  isLoading: runs.isLoading || zones.isLoading || locations.isLoading
             || vehicles.isLoading || featureFlags.isLoading,
  selectedMarket: markets.selectedMarket,
  runs: runs.runs,
  wranglers: wranglers.activeMarketWranglers,
  zones: zones.noDynamicZones,
  allZones: zones.marketZones,
  marketLocations: locations.marketLocations,
  marketWarehouses: locations.activeMarketWarehouseLocations,
  marketVehicles: vehicles.marketVehicles,
  deployableVehicles: vehicles.deployableVehicles,
  featureFlags: featureFlags.featureFlags,
  equityDeployMarkets: settings.equityDeployMarkets,
});

export const RunTable = withPersistentSettings(
  connect(mapStateToProps, mapDispatchToProps)(withRouter((BaseRunTable))),
  'RunTable',
);

export default { RunTable, BaseRunTable };
